import { v4 as uuid } from 'uuid';
import { RECIPE_NODE_NAME, RECIPE_STEP_NODE_NAME } from '../../editor/extensions/recipe';
import { ContentBlockType, RecipeBlock } from '../../model';
import { BlockToTiptapConverter, convertBlocksToTiptapNodes, ConverterContext } from '../blocks-to-tiptap.converter';
import { convertTiptapNodesToBlocks, TiptapToBlockConverter } from '../tiptap-to-blocks.converter';

/**
 * Converts a RecipeBlock to TipTap recipe + recipeStep nodes.
 * The recipe name and step titles are stored as node attributes.
 * Recursively converts nested content blocks within each step.
 */
export const convertRecipeBlockToTiptap = ((block, context?: ConverterContext) => {
  return {
    type: RECIPE_NODE_NAME,
    attrs: {
      id: block.id,
      ...(block.name && { name: block.name }),
      ...(block.has_background !== undefined && { has_background: block.has_background }),
      ...(block.custom_css && { custom_css: block.custom_css }),
      ...(block.custom_style && { custom_style: block.custom_style }),
    },
    content: [
      ...block.steps.map((step) => ({
        type: RECIPE_STEP_NODE_NAME,
        attrs: {
          ...(step.title && { title: step.title }),
        },
        content: step.content.length ? convertBlocksToTiptapNodes(step.content, context) : [{ type: 'paragraph' }],
      })),
    ],
  };
}) satisfies BlockToTiptapConverter<RecipeBlock>;

/**
 * Converts a TipTap recipe node back to a RecipeBlock.
 * Recursively converts nested content within each step.
 */
export const convertTiptapToRecipeBlock = ((json, context?) => {
  const attrs = json.attrs ?? {};
  const children = json.content ?? [];
  const stepNodes = children.filter((n) => n.type === RECIPE_STEP_NODE_NAME);

  const name = (attrs['name'] as string) || '';

  return {
    id: (attrs['id'] as string) ?? uuid(),
    type: ContentBlockType.Recipe,
    ...(name && { name }),
    ...(attrs['has_background'] !== undefined && { has_background: attrs['has_background'] as boolean }),
    ...(attrs['custom_css'] && { custom_css: attrs['custom_css'] as string }),
    ...(attrs['custom_style'] && { custom_style: attrs['custom_style'] as Record<string, string> }),
    steps: stepNodes.map((stepNode) => {
      const stepAttrs = stepNode.attrs ?? {};
      const stepContentNodes = stepNode.content ?? [];
      return {
        title: (stepAttrs['title'] as string) || '',
        content: stepContentNodes.length ? convertTiptapNodesToBlocks(stepContentNodes, context) : [],
      };
    }),
  };
}) satisfies TiptapToBlockConverter;
